"use client";

import * as React from "react";

import { cn } from "@immi/ui/lib/utils";
import { fromCents, parseAmountToCents } from "@immi/ui/lib/money";
import { Input } from "@immi/ui/components/input";

const CURRENCY_PREFIX: Record<string, string> = {
  BRL: "R$",
  USD: "US$",
  CAD: "C$",
  EUR: "€",
};

type MoneyFieldProps = Omit<
  React.ComponentProps<"input">,
  "value" | "defaultValue" | "onChange" | "type"
> & {
  /** Valor em cents — a mesma unidade que a API guarda. */
  defaultValueInCents?: number | null;
  onValueChange?: (cents: number | null) => void;
  currency?: string;
  containerClassName?: string;
};

function toDisplay(cents: number | null | undefined): string {
  if (cents == null) return "";
  return fromCents(cents).toFixed(2).replace(".", ",");
}

function MoneyField({
  defaultValueInCents,
  onValueChange,
  currency = "BRL",
  name,
  className,
  containerClassName,
  ...props
}: MoneyFieldProps) {
  const [display, setDisplay] = React.useState(() =>
    toDisplay(defaultValueInCents),
  );
  const cents = parseAmountToCents(display);
  const prefix = CURRENCY_PREFIX[currency.toUpperCase()] ?? currency;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setDisplay(event.target.value);
    onValueChange?.(parseAmountToCents(event.target.value));
  };

  return (
    <div data-slot="money-field" className={cn("relative", containerClassName)}>
      <span
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-ink-subtle"
      >
        {prefix}
      </span>
      <Input
        type="text"
        inputMode="decimal"
        value={display}
        onChange={handleChange}
        className={cn("pl-11 text-right tabular-nums", className)}
        {...props}
      />
      {name && <input type="hidden" name={name} value={cents ?? ""} />}
    </div>
  );
}

export { MoneyField };
export type { MoneyFieldProps };
